import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@carbon/react";
import DateField from "../../../components/carbon/Form/DateField";
import TextareaField from "../../../components/carbon/Form/TextareaField";
import SelectField from "../../../components/carbon/Form/SelectField";
import TextField from "../../../components/carbon/Form/TextField";
import { useNotification } from "../../../context";
import { isOcConflict, useUpdateOcDraft } from "../../../hooks/useOcActions";
import { PurchaseOrderResponse, UpdateOcDraftRequest } from "../../../types";
import { paymentMethodInCurrentLocale } from "../../../utils";
import {
  buildPaymentMethodOptions,
  INCOTERM_OPTIONS,
  withCurrentValue,
} from "../termOptions";
import { formatOrderTotal } from "../formatOrderTotal";
import "./editOc.scss";

interface EditOcFormFields {
  paymentTerm: string;
  paymentMethod: string;
  incoterm: string;
  notes: string;
  requestedDeliveryDate: string;
  /** Per-line VAT %, as typed, indexed like `oc.lines` (blank = no rate). */
  lineVat: string[];
}

const toFormFields = (oc: PurchaseOrderResponse): EditOcFormFields => ({
  paymentTerm: oc.paymentTerm ?? "",
  // Normalised to the current-locale label so a stored "Transfer"/"Transferencia" pre-selects the
  // matching option; an unrecognised legacy value passes through and is kept as its own option.
  paymentMethod: paymentMethodInCurrentLocale(oc.paymentMethod),
  incoterm: oc.incoterm ?? "",
  notes: oc.notes ?? "",
  requestedDeliveryDate: oc.requestedDeliveryDate ?? "",
  lineVat: oc.lines.map((l) => (l.vatRate == null ? "" : String(l.vatRate))),
});

/** A valid VAT percent: a finite number in 0..100. */
const parseVatRate = (raw: string): number | null => {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
};

/**
 * ALL five keys are always present: the backend PUT is REPLACE, so an omitted key would be
 * cleared server-side. A blank field is sent as `null` (explicitly empty), never left out.
 */
const toRequest = (
  values: EditOcFormFields,
  lines: UpdateOcDraftRequest["lines"]
): UpdateOcDraftRequest => {
  const orNull = (v: string) => (v.trim() === "" ? null : v.trim());
  return {
    paymentTerm: orNull(values.paymentTerm),
    paymentMethod: orNull(values.paymentMethod),
    incoterm: orNull(values.incoterm),
    notes: orNull(values.notes),
    requestedDeliveryDate: orNull(values.requestedDeliveryDate),
    // Only the lines whose VAT changed; the key is left out entirely when none did.
    ...(lines && lines.length > 0 ? { lines } : {}),
  };
};

export interface EditOcModalProps {
  open: boolean;
  onClose: () => void;
  oc: PurchaseOrderResponse;
}

/**
 * "Editar": the DRAFT OC's terms form (payment term/method, incoterm, notes, requested delivery),
 * pre-filled from the OC. Save stays disabled until a field changes (the repo's dirty-gating
 * convention) and submits all five terms in one PUT, plus the per-line VAT rates that changed
 * (net price / quantity are never editable here; the backend recomputes tax). A 409 (the OC is no longer a DRAFT) is an
 * error toast, never a crash.
 */
const EditOcModal = ({ open, onClose, oc }: EditOcModalProps) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const update = useUpdateOcDraft();

  const defaults = useMemo(() => toFormFields(oc), [oc]);
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { isDirty },
  } = useForm<EditOcFormFields>({ defaultValues: defaults });

  const paymentMethodOptions = useMemo(
    () =>
      withCurrentValue(buildPaymentMethodOptions(t), defaults.paymentMethod),
    [t, defaults.paymentMethod]
  );
  const incotermOptions = useMemo(
    () => withCurrentValue(INCOTERM_OPTIONS, defaults.incoterm),
    [defaults.incoterm]
  );

  // Every open starts from the OC's current terms (also re-syncs after a refetch).
  useEffect(() => {
    if (open) reset(defaults);
  }, [open, defaults, reset]);

  const onSubmit = (values: EditOcFormFields) => {
    const changed: NonNullable<UpdateOcDraftRequest["lines"]> = [];
    let invalid = false;
    // Per-line VAT is a DRAFT-only edit.
    const lines = oc.status === "DRAFT" ? oc.lines : [];
    lines.forEach((line, i) => {
      const raw = values.lineVat[i] ?? "";
      if (raw.trim() === defaults.lineVat[i]) return;
      const vatRate = parseVatRate(raw);
      if (vatRate === null) {
        invalid = true;
        setError(`lineVat.${i}`, {
          message: t("purchaseOrders.edit.vatInvalid"),
        });
      } else {
        changed.push({ lineId: line.id, vatRate });
      }
    });
    if (invalid) return;

    update.mutate(
      { id: oc.id, terms: toRequest(values, changed) },
      {
        onSuccess: () => {
          openNotification(t("purchaseOrders.edit.success"));
          onClose();
        },
        onError: (error) =>
          openNotification(
            t(
              isOcConflict(error)
                ? "purchaseOrders.edit.notDraft"
                : "purchaseOrders.edit.error"
            )
          ),
      }
    );
  };

  const title = t("purchaseOrders.edit.title");

  return (
    <ComposedModal open={open} size="md" aria-label={title} onClose={onClose}>
      <ModalHeader title={title} />
      <ModalBody>
        <form
          id="edit-oc-form"
          className="edit-oc__form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <TextField
            name="paymentTerm"
            label={t("purchaseOrders.edit.paymentTerm")}
            control={control}
          />
          <SelectField
            name="paymentMethod"
            label={t("purchaseOrders.edit.paymentMethod")}
            control={control}
            options={paymentMethodOptions}
            placeholder={t("cta.select")}
          />
          <SelectField
            name="incoterm"
            label={t("purchaseOrders.edit.incoterm")}
            control={control}
            options={incotermOptions}
            placeholder={t("cta.select")}
          />
          <DateField
            name="requestedDeliveryDate"
            label={t("purchaseOrders.edit.requestedDelivery")}
            control={control}
          />
          <TextareaField
            name="notes"
            label={t("purchaseOrders.edit.notes")}
            control={control}
            rows={3}
          />

          {oc.status === "DRAFT" && oc.lines.length > 0 ? (
            <fieldset className="edit-oc__vat">
              <legend className="edit-oc__vat-legend">
                {t("purchaseOrders.edit.vatTitle")}
              </legend>
              <p className="edit-oc__vat-hint">
                {t("purchaseOrders.edit.vatHint")}
              </p>
              {oc.lines.map((line, i) => (
                <TextField
                  key={line.id}
                  name={`lineVat.${i}` as const}
                  label={t("purchaseOrders.edit.vatLine", {
                    material:
                      line.materialName ??
                      line.description ??
                      t("purchaseOrders.edit.vatLineFallback", { n: i + 1 }),
                  })}
                  control={control}
                  type="number"
                  min={0}
                  max={100}
                  step="any"
                  helperText={
                    line.lineTax != null
                      ? t("purchaseOrders.edit.vatLineTax", {
                          net: formatOrderTotal(line.lineTotal, oc.currency),
                          tax: formatOrderTotal(line.lineTax, oc.currency),
                        })
                      : t("purchaseOrders.edit.vatLineNet", {
                          net: formatOrderTotal(line.lineTotal, oc.currency),
                        })
                  }
                />
              ))}
              <dl className="edit-oc__totals">
                <div>
                  <dt>{t("purchaseOrders.detail.net")}</dt>
                  <dd>{formatOrderTotal(oc.totalAmount, oc.currency)}</dd>
                </div>
                {oc.taxAmount != null ? (
                  <div>
                    <dt>{t("purchaseOrders.detail.tax")}</dt>
                    <dd>{formatOrderTotal(oc.taxAmount, oc.currency)}</dd>
                  </div>
                ) : null}
                {oc.grossAmount != null ? (
                  <div>
                    <dt>{t("purchaseOrders.detail.gross")}</dt>
                    <dd>{formatOrderTotal(oc.grossAmount, oc.currency)}</dd>
                  </div>
                ) : null}
              </dl>
            </fieldset>
          ) : null}
        </form>
      </ModalBody>
      <ModalFooter
        secondaryButtonText={t("cta.cancel")}
        primaryButtonText={t("purchaseOrders.edit.save")}
        loadingStatus={update.isPending ? "active" : "inactive"}
        loadingDescription={t("cta.loading")}
        primaryButtonDisabled={update.isPending || !isDirty}
        onRequestSubmit={handleSubmit(onSubmit)}
      >
        {null}
      </ModalFooter>
    </ComposedModal>
  );
};

export default EditOcModal;
